<script lang="ts">
import { formateDate } from '@/app/common/dateFormate';
import { ATTACHMENT_OPTIONS } from '@/app/const';
import { ComplaintAdditionalDoc } from '@/interface/Complaint.interface';
import { complaintService, fileService } from '@/services/serviceProvider';

export default {
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-complaint-additional-docs'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        confirmText: {
            type: String,
            default: 't-approve'
        },
        complaintAdditionalDocs: {
            type: Array<Object>,
            default: []
        },
        action: {
            type: String,
            default: undefined
        },
        viewFile: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'onConfirm', 'onCancel'],
    setup() {
        const fileSv = fileService;
        return { fileSv }
    },
    data() {
        return {
            additionalDocs: this.complaintAdditionalDocs as ComplaintAdditionalDoc[],
            attachmentOptions: ATTACHMENT_OPTIONS,
        }
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        headers() {
            return [
                { title: this.$t('t-order'), key: 'index', width: '5%', align: 'center', sortable: false },
                { title: this.$t('t-complaint-additional-docs'), key: 'docName' },
                { title: this.$t('t-complaint-request-by-dpm'), key: 'departmentCode', width: '15%' },
                { title: this.$t('t-complaint-request-date'), key: 'requestDateString', width: '16%' },
            ] as any[];
        },
        headerWithView() {
            return [
                { title: this.$t('t-order'), key: 'index', width: '5%', align: 'center', sortable: false },
                { title: this.$t('t-complaint-additional-docs'), key: 'docName' },
                { title: this.$t('t-complaint-request-by-dpm'), key: 'departmentCode', width: '12%' },
                { title: this.$t('t-complaint-request-date'), key: 'requestDateString', width: '16%' },
                { title: this.$t('t-complaint-receive-date'), key: 'receiveDateString', width: '17%' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
        allowView() {
            return this.viewFile;
        }
    },
    methods: {
        confirm() {
            this.dialogShow = false;
            this.$emit('onConfirm', this.action);
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel')
        },
        getDateString(date: Date | undefined) {
            return complaintService.dateString(date);
        },
        attachmentLabel(value: number, remark: string | number | undefined) {
            if (value === 1 || value === 2) {
                return this.$t(`t-${this.attachmentOptions.find(o => o.value === value)?.label}`)
            }
            return `${this.$t(`t-complaint-req-doc-other`)} (${remark})`;
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="65%" :close-on-back="false" persistent>
        <v-card class="dialog-card">
            <v-toolbar density="compact">
                <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <v-btn icon="mdi-close" @click="dialogShow = false"></v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <v-divider class="mx-4"></v-divider>
            <v-card-text>
                <v-data-table :headers="allowView ? headerWithView : headers" :items="additionalDocs"
                    hide-default-footer :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                    <template #[`item.index`]="{ index }">
                        <div class="w-100 text-center">{{ index + 1 }}</div>
                    </template>
                    <template #[`item.docName`]="{ item }">
                        <div class="w-100">{{ attachmentLabel(item.docTypeNo, item.remark) }}</div>
                    </template>
                    <template #[`item.requestDateString`]="{ item }">
                        <div class="w-100">{{ getDateString(item.requestDate) }}</div>
                    </template>
                    <template #[`item.receiveDateString`]="{ item }">
                        <div class="w-100">{{ getDateString(item.receiveDate) }}</div>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <div class="actions d-flex align-center">
                            <v-btn v-if="item.attachments && item.attachments.length === 1" variant="text"
                                color="primary"
                                @click="fileSv.viewFileAlreadySaved(item.attachments[0].url, item.attachments[0].fileName)">
                                <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                <span>{{ $t('t-view-doc') }}</span>
                            </v-btn>
                            <v-menu v-if="item.attachments && item.attachments.length > 1">
                                <template v-slot:activator="{ props }">
                                    <v-btn variant="text" color="primary" v-bind="props">
                                        <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                        <span>{{ $t('t-view-doc') }}</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item v-for="(file, index) in item.attachments" :key="index" :value="index"
                                        @click="fileSv.viewFileAlreadySaved(file.url, file.fileName)">
                                        <v-list-item-title>{{ file.fileName }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-container class="mx-0">
                    <div class="d-flex justify-end">
                        <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                            {{ $t(cancelText) }}
                        </v-btn>
                        <v-btn @click="confirm" variant="flat" color="primary" size="large">
                            {{ $t(confirmText) }}
                        </v-btn>
                    </div>
                </v-container>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>